import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FloppyDisk, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = [
  { key: "cash_on_hand", label: "Cash on hand", section: "assets" },
  { key: "bank_balance", label: "Bank balance", section: "assets" },
  { key: "fixed_assets", label: "Fixed assets (net)", section: "assets" },
  { key: "other_assets", label: "Other assets", section: "assets" },
  { key: "loans_payable", label: "Loans payable", section: "liab" },
  { key: "other_liabilities", label: "Other liabilities", section: "liab" },
  { key: "opening_capital", label: "Opening capital / owner equity", section: "equity" },
];
// Precompute per-section arrays once — data is static so no need to filter on every render.
const ASSET_FIELDS  = FIELDS.filter((f) => f.section === "assets");
const LIAB_FIELDS   = FIELDS.filter((f) => f.section === "liab");
const EQUITY_FIELDS = FIELDS.filter((f) => f.section === "equity");

export default function OpeningBalances() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/opening-balances");
    setValues(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...values };
      delete body.id;
      delete body.updated_at;
      const { data } = await api.put("/opening-balances", body);
      setValues(data);
      toast.success("Opening balances saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!values) return <div className="p-8 text-slate-500">Loading…</div>;

  const totalAssets = ["cash_on_hand", "bank_balance", "fixed_assets", "other_assets"].reduce((s, k) => s + Number(values[k] || 0), 0);
  const totalLiab = ["loans_payable", "other_liabilities"].reduce((s, k) => s + Number(values[k] || 0), 0);
  const totalEquity = Number(values.opening_capital || 0);
  const diff = totalAssets - (totalLiab + totalEquity);

  return (
    <div data-testid="opening-balances-page">
      <PageHeader
        title="Opening Balances"
        module="Finance · Setup"
        subtitle="One-time snapshot of what your business owes and owns on the FY-start date. Enables an accurate Balance Sheet."
        ribbon={
          <>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
              <BcRibbonBtn icon={FloppyDisk} label="Save" variant="primary" onClick={save} disabled={saving} testid="ribbon-save-opening" />
            </BcRibbonGroup>
          </>
        }
      />

      <div className="p-6 max-w-3xl">
        <div className="bc-card p-5 space-y-5">
          <div>
            <Label className="label-caps mb-1 block">As of date (FY start)</Label>
            <Input
              type="date"
              value={values.as_of_date || "2026-04-01"}
              onChange={(e) => setValues({ ...values, as_of_date: e.target.value })}
              data-testid="opening-asof-input"
              className="w-44"
            />
          </div>

          <SectionBlock title="Assets" tone="assets">
            {ASSET_FIELDS.map((f) => (
              <Row key={f.key} field={f} values={values} setValues={setValues} />
            ))}
            <Total label="Total Assets" value={totalAssets} tone="assets" />
          </SectionBlock>

          <SectionBlock title="Liabilities" tone="liab">
            {LIAB_FIELDS.map((f) => (
              <Row key={f.key} field={f} values={values} setValues={setValues} />
            ))}
            <Total label="Total Liabilities" value={totalLiab} tone="liab" />
          </SectionBlock>

          <SectionBlock title="Equity" tone="equity">
            {EQUITY_FIELDS.map((f) => (
              <Row key={f.key} field={f} values={values} setValues={setValues} />
            ))}
            <Total label="Total Equity" value={totalEquity} tone="equity" />
          </SectionBlock>

          <div className={`p-3 rounded-[2px] text-[12.5px] flex items-center justify-between ${Math.abs(diff) < 1 ? "bg-[#DFF6DD] text-[#107C10] border border-emerald-200" : "bg-[#FFF4CE] text-[#8A6E00] border border-amber-200"}`}>
            <span className="font-semibold">Balance check</span>
            <span className="font-mono-num">{Math.abs(diff) < 1 ? "Balanced ✓" : `Δ ${fmtMoney(diff)} (Assets − Liabilities+Equity)`}</span>
          </div>

          <Button onClick={save} disabled={saving} data-testid="save-opening-balances" className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
            {saving ? "Saving…" : "Save Opening Balances"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ field, values, setValues }) {
  return (
    <div className="grid grid-cols-2 gap-3 items-center">
      <Label className="text-[12.5px] text-[#323130]">{field.label}</Label>
      <Input
        type="number"
        value={values[field.key] || 0}
        onChange={(e) => setValues({ ...values, [field.key]: Number(e.target.value) || 0 })}
        data-testid={`opening-${field.key}`}
        className="font-mono-num"
      />
    </div>
  );
}

function SectionBlock({ title, tone, children }) {
  const border = { assets: "border-emerald-200", liab: "border-red-200", equity: "border-sky-200" }[tone];
  return (
    <div className={`border ${border} rounded-[2px] p-4 space-y-3`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#605E5C]">{title}</div>
      {children}
    </div>
  );
}

function Total({ label, value, tone }) {
  const color = { assets: "text-emerald-700", liab: "text-red-700", equity: "text-[#2E75B6]" }[tone];
  return (
    <div className="grid grid-cols-2 gap-3 items-center pt-2 border-t border-dashed border-[#EDEBE9]">
      <div className="font-semibold text-[13px] text-[#201F1E]">{label}</div>
      <div className={`text-right font-display font-bold text-[15px] font-mono-num ${color}`}>{fmtMoney(value)}</div>
    </div>
  );
}
